import { z } from 'zod';
import { safeParseJson } from './parse-json';

/**
 * Reads and validates one persisted value. A missing key, a storage error,
 * malformed JSON, or a wrong shape all return `fallback`.
 */
export function readStoredJson<S extends z.ZodType>(
  storage: Pick<Storage, 'getItem'>,
  key: string,
  schema: S,
  fallback: z.infer<S>,
): z.infer<S> {
  let text: string | null;
  try {
    text = storage.getItem(key);
  } catch {
    return fallback;
  }
  if (text === null) return fallback;
  const parsed = safeParseJson(schema, text);
  return parsed.success ? parsed.data : fallback;
}

/** Reads one raw string. A missing key or a storage error returns `null`. No parsing. */
export function readStoredString(storage: Pick<Storage, 'getItem'>, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/** An array that keeps the items matching `item` and drops the rest. */
export function tolerantArray<T>(item: z.ZodType<T>): z.ZodType<T[], unknown> {
  return z.array(z.unknown()).transform((items) =>
    items.flatMap((entry) => {
      const parsed = item.safeParse(entry);
      return parsed.success ? [parsed.data] : [];
    }),
  );
}
